"use client"
import { type IPaymentQueryParams } from "@/lib/centra-client/interfaces/IPaymentService"
import { serializeError } from "@/lib/centra-client/serializeError"
import logger from "@/lib/logger/logger-init"
import { type CheckoutAPIPaymentnWithRequiredToken } from "@/types/centra/checkout-api/payment/payment-response"
import { type BasketSession } from "@/types/cookie-types"
import { type Result, ResultFactory } from "@/types/generic/Result"

function extractFirstJsonObject<T = unknown>(input: string): Result<T> {
    if (input.length === 0) {
        return ResultFactory.failure<T>(new Error("Input is empty"))
    }

    const start = input.indexOf("{")
    if (start === -1) {
        return ResultFactory.failure<T>(new Error("No '{' found in input"))
    }

    const last = input.lastIndexOf("}")
    if (last === -1 || last < start) {
        return ResultFactory.failure<T>(new Error("JSON braces not found or invalid"))
    }

    try {
        const parsed = JSON.parse(input.slice(start, last + 1)) as T
        return ResultFactory.success<T>(parsed)
    } catch (error) {
        return ResultFactory.failure<T>(
            error instanceof Error ? error : new Error("Failed to parse JSON")
        )
    }
}

export type PaymentParams = {
    paymentResult?: CheckoutAPIPaymentnWithRequiredToken
    extractedBasketSessionCookie?: BasketSession
}

export const payment = async (
    formData: IPaymentQueryParams
): Promise<Result<PaymentParams>> => {
    try {
        const response = await fetch("/api/centra/payment", {
            method: "POST",
            credentials: "include",
            headers: {
                "Content-Type": "application/json"
            },
            body: JSON.stringify(formData)
        })

        const result = await response.json()

        if (!response.ok || !result.success) {
            const errorMessage = result.error || "Failed to initiate payment"
            const errorDetails = result.details || {}

            logger.warn("Failed to initiate payment", {
                error: errorMessage,
                details: errorDetails,
                status: response.status,
                statusText: response.statusText
            })

            let errorDetailsParsed = errorDetails
            if (typeof errorMessage === "string") {
                const parsedResult = extractFirstJsonObject(errorMessage)
                if (parsedResult._tag === "success") {
                    errorDetailsParsed = parsedResult.data
                }
            }

            return ResultFactory.failure<PaymentParams>(
                new Error(errorMessage, {
                    cause: errorDetailsParsed
                })
            )
        }
        logger.info("Payment initiated successfully")

        return ResultFactory.success<PaymentParams>({
            paymentResult: result.data.paymentResult,
            extractedBasketSessionCookie: result.data.extractedBasketSessionCookie
        })
    } catch (error) {
        logger.error("Unexpected error in initiatePayment", error)
        return ResultFactory.failure<PaymentParams>(
            serializeError(
                error instanceof Error ? error : new Error("An unknown error occurred")
            )
        )
    }
}
